// frontend/modules/sms/components/SmsBalanceCard.tsx
import React from 'react';
import { Card, Statistic, Skeleton, Tag, Row, Col, Typography } from 'antd';
import { MessageOutlined, CreditCardOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';

const { Text } = Typography;

interface Props {
    balance: number | null;
    isLoading: boolean;
    onRefresh?: () => void;
}

const SmsBalanceCard: React.FC<Props> = ({ balance, isLoading, onRefresh }) => {
    const { dark } = useTheme();

    const getBalanceStatus = (b: number) => {
        if (b >= 1000) return { color: 'success', label: 'Healthy' };
        if (b >= 100) return { color: 'warning', label: 'Low' };
        return { color: 'error', label: 'Critical' };
    };

    const status = balance !== null ? getBalanceStatus(balance) : null;

    return (
        <Card
            className={dark ? 'bg-gray-700 text-white' : ''}
            extra={
                onRefresh && (
                    <ReloadOutlined
                        spin={isLoading}
                        onClick={onRefresh}
                        style={{ cursor: 'pointer' }}
                        title="Refresh balance"
                    />
                )
            }
        >
            <Row gutter={[16, 16]} align="middle">
                <Col xs={24} sm={12}>
                    {isLoading ? (
                        <Skeleton active paragraph={{ rows: 1 }} />
                    ) : (
                        <Statistic
                            title={
                                <Text className={dark ? 'text-gray-300' : ''}>
                                    <CreditCardOutlined className="mr-1" />
                                    SMS Credits Remaining
                                </Text>
                            }
                            value={balance ?? 0}
                            prefix={<MessageOutlined />}
                            valueStyle={{
                                color: balance !== null && balance < 100
                                    ? '#ff4d4f'
                                    : balance !== null && balance < 1000
                                    ? '#faad14'
                                    : '#52c41a',
                            }}
                        />
                    )}
                </Col>
                {status && !isLoading && (
                    <Col xs={24} sm={12} className="flex items-center">
                        <Tag color={status.color} style={{ fontSize: 14, padding: '4px 12px' }}>
                            {status.label}
                        </Tag>
                    </Col>
                )}
            </Row>
        </Card>
    );
};

export default SmsBalanceCard;
